import { IconButton, Menu, MenuItem } from "@mui/material";
import React, { useState } from "react";
import { MdMoreVert, MdNotifications } from "react-icons/md";
import api from "../../api/axiosInstance";
import { UserListProps, UserRolesEnum } from "../../typings/users";
import ConfirmationDialog from "../../components/atoms/ConfirmationDialog";
import { Box, Typography } from "@mui/material";
import { useSnackbar } from "../../context/SnackbarContext";

type UserActionMenuProps = {
  user: UserListProps;
};

export const UserActionMenu: React.FC<UserActionMenuProps> = ({ user }) => {
  const { showSnackbar } = useSnackbar();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [reminderLoading, setReminderLoading] = useState(false);

  const isDonor = user.role === UserRolesEnum.DONOR;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOpenReminderDialog = () => {
    setReminderDialogOpen(true);
    handleClose();
  };

  const handleSendReminder = async () => {
    setReminderLoading(true);
    try {
      await api.post("/scholarship-applications/donor-contribution-reminder/", {
        donor_id: user.id,
      });
      setReminderDialogOpen(false);
      showSnackbar(`Donation reminder sent to ${user.name} successfully.`, "success");
    } catch (error: any) {
      const message =
        error?.response?.data?.messages?.[0] ||
        "Failed to send donation reminder. Please try again.";
      showSnackbar(message, "error");
    } finally {
      setReminderLoading(false);
    }
  };

  return (
    <>
      {/* Always render the same-sized slot for alignment.
          Only show the button (and attach interactivity) for donors.  */}
      <IconButton
        aria-label="User Actions"
        aria-controls={open ? "user-action-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={isDonor ? handleClick : undefined}
        size="small"
        disableRipple={!isDonor}
        sx={{
          height: "32px",
          width: "32px",
          visibility: isDonor ? "visible" : "hidden", // takes space but is invisible for non-donors
          pointerEvents: isDonor ? "auto" : "none",
        }}
      >
        <MdMoreVert style={{ height: "16px", width: "16px" }} />
      </IconButton>

      {isDonor && (
        <>
          <Menu
            id="user-action-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            MenuListProps={{
              "aria-labelledby": "user-actions-button",
              style: { width: "220px" },
            }}
            PaperProps={{
              style: {
                borderRadius: "8px",
                boxShadow:
                  "0 8px 24px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.06)",
              },
            }}
          >
            <MenuItem
              onClick={handleOpenReminderDialog}
              style={{ fontSize: "14px", padding: "10px 14px", gap: "10px" }}
            >
              <MdNotifications style={{ height: "16px", width: "16px", flexShrink: 0 }} />
              Send Donation Reminder
            </MenuItem>
          </Menu>

          {/* Confirmation dialog */}
          <ConfirmationDialog
            open={reminderDialogOpen}
            onClose={() => setReminderDialogOpen(false)}
            onConfirm={handleSendReminder}
            title="Send Donation Reminder"
            message="You are about to send a donation contribution reminder to the following donor. They will be notified via email."
            confirmText="Send Reminder"
            cancelText="Cancel"
            confirmColor="primary"
            loading={reminderLoading}
          >
            {/* Donor detail card */}
            <Box
              sx={{
                borderRadius: "10px",
                border: "1px solid rgba(32, 99, 71, 0.15)",
                bgcolor: "rgba(32, 99, 71, 0.03)",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                }}
              >
                {[
                  { label: "Name", value: user.name },
                  { label: "Email", value: user.email || "N/A" },
                  { label: "Role", value: user.role },
                  { label: "Phone", value: user.phone_number || "N/A" },
                ].map((field, idx) => (
                  <Box
                    key={field.label}
                    sx={{
                      px: 2,
                      py: 1.25,
                      borderRight:
                        idx % 2 === 0
                          ? "1px solid rgba(32,99,71,0.1)"
                          : "none",
                      borderBottom:
                        idx < 2 ? "1px solid rgba(32,99,71,0.1)" : "none",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#9CA3AF",
                        fontWeight: 500,
                        display: "block",
                        mb: 0.25,
                        fontSize: "0.7rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                      }}
                    >
                      {field.label}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#111827",
                        fontWeight: 600,
                        fontSize: "0.82rem",
                        wordBreak: "break-word",
                      }}
                    >
                      {field.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </ConfirmationDialog>
        </>
      )}
    </>
  );
};
